// [slug].js
import Image from "next/legacy/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import NoSsr from "@mui/material/NoSsr";

// Sanity
import groq from "groq";
import { PortableText } from "@portabletext/react";
import client from "../../sanityClient";
import { useNextSanityImage } from "next-sanity-image";
import imageUrlBuilder from "@sanity/image-url";

// Component
import SeoHead from "../../components/SeoHead";

// import ReactPlayer from "react-player/youtube";
const ReactPlayer = dynamic(() => import("react-player/youtube"));
const SearchTool = dynamic(() => import("../../components/SearchTool"));
const ArticleRecommendations = dynamic(() => import("../../components/ArticleRecommendations"));
const EmailSignupPopup = dynamic(() => import("../../components/EmailSignupPopup"));
const EmailSignupArticleEmbed = dynamic(() => import("../../components/EmailSignupArticleEmbed"));
const SocialShareButtons = dynamic(() => import("../../components/SocialShareButtons"));
// const BlogComments = dynamic(() => import("../../components/BlogComments"));

// MUI
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Breadcrumbs from "@mui/material/Breadcrumbs";

// Styles
const breadcrumbsHeader = {
  textAlign: "left",
  marginTop: "1vh",
  marginBottom: "1vh",
  paddingLeft: "6vw",
  paddingRight: "6vw",
};

const contentBody = {
  textAlign: "left",
  marginTop: "3vh",
  marginBottom: "2vh",
  minHeight: "100vh",
  paddingLeft: "6vw",
  paddingRight: "6vw",
};

const h1 = {
  paddingTop: "5vh",
  fontSize: "2rem",
  paddingBottom: "2px",
  fontWeight: "bold",
};

const articleText = {
  fontSize: "1.3rem",
  overflowWrap: "break-word"
};

const postMetaDataGrid = {
  paddingBottom: "2vh",
};

const imageContainer = {
  width: "100%",
  position: "unset !important",
};

const mainImage = {
  objectFit: "contain",
  width: "100% !important",
  position: "relative !important",
  height: "unset !important",
};

const h2Titles = {
  color: "text.secondary",
  fontSize: "2.5rem",
  marginBottom: "2vh",
};

const searchContainer = {
  width: "100%",
  backgroundColor: "background.secondary",
  textAlign: "center",
  align: "center",
  justify: "center",
  margin: "0",
  color: "text",
  fontSize: "2.5rem",
  padding: "2vh",
  marginTop: "8vh",
  marginBottom: "5vh",
  paddingTop: "5vh",
  paddingBottom: "3vh",
  WebkitBoxShadow: "2px 2px 15px rgba(0,0,0,0.4)",
  MozBoxShadow: "2px 2px 15px rgba(0,0,0,0.4)",
};

// const comments = {
//   textAlign: "center",
//   align: "center",
//   justify: "center",
//   fontSize: "2.5rem",
//   paddingTop: "5vh",
//   paddingBottom: "3vh",
// };

const articleSuggestionsContainer = {
  width: "100%",
  backgroundColor: "background",
  textAlign: "center",
  align: "center",
  justify: "center",
  color: "text",
  fontSize: "2.5rem",
  paddingTop: "5vh",
  paddingBottom: "5vh",
};

const shareText = {
  marginTop: "4vh",
  fontSize: 22,
};

const builder = imageUrlBuilder(client);
function displayPreviewImage(sanityImageUrl) {
  return builder.image(sanityImageUrl).width(1200).height(630).fit("clip").url();
}

function displayTwitterPreviewImageSrc(sanityImageUrl) {
  return builder.image(sanityImageUrl).width(800).height(400).fit("clip").url();
}

const Post = ({ post, slug, posts }) => {
  const previewImageSrc = displayPreviewImage(post.mainImage);
  const twitterPreviewImageSrc = displayTwitterPreviewImageSrc(post.mainImage);
  const canonicalLink = `https://searchexhaust.com/blog/${slug}`;

  function addJsonLd() {
    return {
      __html: `{ 
        "@context": "https://schema.org", 
        "@type": "Article",
        "headline": "${post.title}",
        "image": "${previewImageSrc}",
        "genre": "${post.categories}", 
        "keywords": "${post.categories}", 
        "publisher": "SearchExhaust.com",
        "url": "${canonicalLink}",
        "datePublished": "${post.publishedAt}",
        "dateCreated": "${post.publishedAt}",
        "dateModified": "${post.publishedAt}",
        "description": "${post.metaDescription}",
        "articleBody": "placeholder",
        "author": {
          "@type": "Person",
          "name": "${post.name}"
        }
      }`,
    };
  }

  function addBreadcrumbJsonLd() {
    return {
      __html: `{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://searchexhaust.com"
        },{
          "@type": "ListItem",
          "position": 2,
          "name": "Blog",
          "item": "https://searchexhaust.com/blog"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": ${post.title},
          "item": ${canonicalLink}
        }]
      }`,
    };
  }

  const breadcrumbs = [
    <Link underline="hover" key="1" color="inherit" href="/" prefetch={false}>
      Search Exhaust
    </Link>,
    <Link underline="hover" key="2" color="inherit" href="https://searchexhaust.com/blog" prefetch={false}>
      Blog
    </Link>,
  ];

  const ptComponents = {
    types: {
      image: ({ value }) => {
        if (!value?.asset?._ref) {
          return null;
        }
        return (
          <Image
            sx={mainImage}
            priority={true}
            {...useNextSanityImage(client, value)}
            sizes="(max-width: 800px) 60vw, 800px"
            alt={post.title}
          />
        );
      },
      youtube: ({ value }) => {
        if (!value?.url) {
          return null;
        }
        return <ReactPlayer url={value.url} controls={true} width={"100%"} light={true} />;
      },
      imageBlock: ({ value }) => {
        if (!value?.asset?._ref) {
          return null;
        }
        if (value?.caption) {
          return (
            <>
              <Image
                sx={mainImage}
                priority={false}
                {...useNextSanityImage(client, value)}
                sizes="(max-width: 800px) 60vw, 800px"
                alt={value.alt}
              />
              <Typography variant="caption">({value.caption})</Typography>
            </>
          );
        }
        return (
          <Image
            sx={mainImage}
            priority={false}
            {...useNextSanityImage(client, value)}
            sizes="(max-width: 800px) 60vw, 800px"
            alt={value.alt}
          />
        );
      },
      affiliateProduct: ({ value }) => {
        const codeForPage = `<HTML><BODY>${value.nativeShoppingScript.code}</BODY></HTML>`;
        if (!value?.asset?._ref) {
          return (
            <NoSsr>
              <iframe srcDoc={codeForPage} frameBorder="0" scrolling="no" width="100%"></iframe>
            </NoSsr>
          );
        }
      },
      autoProductReference: ({ value }) => {
        const codeForPage = `<HTML><BODY>${value.nativeShoppingScript.code}</BODY></HTML>`;
        if (!value?.asset?._ref) {
          return (
            <NoSsr>
              <iframe srcDoc={codeForPage} frameBorder="0" scrolling="no" height="525px" width="100%"></iframe>
            </NoSsr>
          );
        }
      },
    },
  };

  if (post) {
    const {
      title = "Missing title",
      name = "Missing name",
      publishedAt = "Missing published date",
      metaDescription = "",
      categories,
      authorImage,
      body = [],
    } = post;

    return (
      <article>
        <SeoHead
          title={title}
          description={metaDescription}
          canonicalLink={canonicalLink}
          imageSrc={previewImageSrc}
          twitterImageSrc={twitterPreviewImageSrc}
          jsonLd={addJsonLd()}
          breadcrumbJsonLd={addBreadcrumbJsonLd()}
        />
        <Breadcrumbs sx={breadcrumbsHeader} separator="›" aria-label="breadcrumb">
          {breadcrumbs}
        </Breadcrumbs>
        <Container maxWidth="md" sx={contentBody}>
          <Box alignItems="center" justifyContent="center" sx={imageContainer}>
            {post.mainImage && (
              <Image
                sx={mainImage}
                priority={true}
                {...useNextSanityImage(client, post.mainImage)}
                sizes="(max-width: 800px) 100vw, 800px"
                alt={post.title}
              />
            )}
          </Box>
          <Typography variant="h1" sx={h1}>
            {post.title}
          </Typography>

          <Grid container sx={postMetaDataGrid} spacing={2} alignItems="center" justifyContent="left">
            <Grid item sx={{ width: "80px" }}>
              {post.authorImage && (
                <Box sx={imageContainer}>
                  <Image
                    {...useNextSanityImage(client, post.authorImage)}
                    sizes="(max-width: 80px) 80px, 80px"
                    alt={post.title}
                  />
                </Box>
              )}
            </Grid>
            <Grid item>
              <Grid container spacing={0} direction="column">
                <Grid item>
                  <Typography variant="body1">By {post.name}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1">{new Date(post.publishedAt).toDateString()}</Typography>
                </Grid>
                <Grid item>
                  {post.categories && (
                    <div>
                      {post.categories.map((category) => (
                        <Typography key={category}>
                          <i>{category}</i>
                        </Typography>
                      ))}
                    </div>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Box sx={articleText}>
            <PortableText value={post.body} components={ptComponents} />
          </Box>
          <hr />
          <EmailSignupArticleEmbed page={canonicalLink} />
          <Typography variant="body1" sx={shareText}>
            Did you find this content useful? If so, please give it a share, thank you. 😊
          </Typography>
          <SocialShareButtons url={canonicalLink} title={post.title} />
          {/* <Box sx={comments}>
            <BlogComments pageId={slug} pageTitle={slug} pageUrl={canonicalLink} />
          </Box> */}
        </Container>
        <Box sx={searchContainer}>
          <Container maxWidth="sm">
            <Typography variant="h2" sx={h2Titles}>
              Discover Your Perfect Exhaust!
            </Typography>
            <Typography>
              We hope you enjoyed that article. Now that you've learned something new, try out THE MOST POPULAR aftermarket exhaust search
              on the internet. You're going to find something cool, we promise.
            </Typography>
          </Container>
          <SearchTool />
        </Box>
        <Box sx={articleSuggestionsContainer}>
          <Typography variant="h2" sx={h2Titles}>
            Our Most Popular Articles Right Now
          </Typography>
          <ArticleRecommendations posts={posts} />
        </Box>
        <EmailSignupPopup delaySeconds={30} page={canonicalLink} />
      </article>
    );
  }
};

// Resolve references inside block content: https://stackoverflow.com/questions/65184614/resolve-reference-in-block-content-in-sanity-io
const query = groq`* [_type == "post" && slug.current == $slug][0]{
              title,
              publishedAt,
              metaDescription,
              mainImage,
              "name": author->name,
              "categories": categories[]->title,
              "authorImage": author->image,
              body[]{
                _type == "reference"=>@->,
                _type == "autoProductReference"=>@->,
                _type != 'reference' => @,
              }
  }`;

export async function getStaticPaths() {
  const paths = await client.fetch(groq`*[_type == "post" && defined(slug.current)][].slug.current`);
  return {
    paths: paths.map((slug) => ({ params: { slug } })),
    fallback: false,
  };
}

export async function getStaticProps(context) {
  // It's important to default the slug so that it doesn't return "undefined"
  const { slug = "" } = context.params;
  const post = await client.fetch(query, { slug });

  // ToDo: Only top 5
  const posts = await client.fetch(groq`
            *[_type == "post" && publishedAt < now()] | order(publishedAt desc)
            `);

  return {
    props: {
      post,
      slug,
      posts,
    },
  };
}
export default Post;
